import type { ReactNode } from 'react'
import { cva } from 'class-variance-authority'
import { FolderOpen, SearchX, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EmptyStateVariant, IntuiEmptyStateProps } from './types'

/**
 * Figma: Caliper Design System — Empty
 *
 * Layout: vertical flex, centered, gap-4, p-6, rounded-lg
 * Title: text-sm / font-medium / text-foreground
 * Description: text-sm / font-normal / leading-[1.625] / text-muted-foreground
 * Icon container: bg-muted / rounded-lg / size-8 / 16px icon
 */
const emptyStateVariants = cva(
  'flex flex-col items-center gap-4 p-6 rounded-lg text-center',
  {
    variants: {
      // Visual differentiation per variant is handled by the default icon.
      // Extend these with background/border classes when the design system evolves.
      variant: {
        'no-data': '',
        'no-results': '',
        error: '',
      },
    },
    defaultVariants: {
      variant: 'no-data',
    },
  }
)

const defaultIcons: Record<EmptyStateVariant, ReactNode> = {
  'no-data': <FolderOpen data-testid="icon-no-data" className="size-4 text-muted-foreground" />,
  'no-results': <SearchX data-testid="icon-no-results" className="size-4 text-muted-foreground" />,
  error: <AlertCircle data-testid="icon-error" className="size-4 text-muted-foreground" />,
}

function IntuiEmptyState({
  variant = 'no-data',
  icon,
  title,
  description,
  actions,
  className,
}: IntuiEmptyStateProps) {
  const resolvedIcon = icon ?? defaultIcons[variant]

  return (
    <div className={cn(emptyStateVariants({ variant }), className)}>
      {/* Header: media + text */}
      <div className="flex flex-col items-center gap-4 w-full">
        {/* Icon container */}
        <div className="flex items-center justify-center size-8 rounded-lg bg-muted">
          {resolvedIcon}
        </div>

        {/* Title + description */}
        <div className="flex flex-col items-center gap-2 w-full text-sm">
          <p role="heading" aria-level={3} className="font-medium leading-5 text-foreground w-full">
            {title}
          </p>
          {description && (
            <p className="font-normal leading-[1.625] text-muted-foreground w-full">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Action slot */}
      {actions && (
        <div className="flex flex-col items-center gap-4 w-full">
          {actions}
        </div>
      )}
    </div>
  )
}

export { IntuiEmptyState, emptyStateVariants }
